import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IAppointment } from '../API/GoMoto';
import { isElectron } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { reverseEnum } from '../helpers/enum';
import { loadScript } from '../helpers/LoadScript/LoadScript';
import { useKioskState } from './Kiosk/kiosk';
import { ILockerFixture, useLockerContext, useSetLockerStatus } from './lockers';
import { isDevelopment } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { ActionMap, GoMotoFunction } from '../API/GoMoto.types';

type MZeroProviderProps = { children: React.ReactNode; };

type InitialStateType = {
    mzero?: typeof mzero;
    error?: string;
    /** Whether mzero has been loaded and is ready to use. */
    connected?: boolean;
    licenseScanEnabled?: boolean;
    lockersEnabled?: boolean;
};

type InternalStateType = {
    /** Whether the mzero scripts have executed. */
    initialized: boolean;
    /** Whether mzero has been loaded but not necessarily initialized. */
    loaded: boolean;
    /** Whether mzero has been configured */
    configured: boolean;
};

const initialState = {};
const MZeroStateContext = React.createContext<InitialStateType>(initialState);
interface IEndableRequest {
    end: () => void;
}
type MZRequest<T = any> = {
    start: (eventHandlers: IMzeroEventHandlers<T>) => void;
    Done: boolean;
} & IEndableRequest;

interface ILockerParams {
    Locker: number;
    DoorClosureTimeout: number;
    APIReport?: {
        CustomRef1?: string;
        CustomId1?: string;
    };
}

interface IMzeroEventHandlers<Data> {
    Done?: (err: string, data: Data) => void;
}

interface IMzeroService {
    State?: 'Available';
}
interface IMzeroServiceMapping {
    ServicesMapping: {
        License?: IMzeroService;
        Locker?: IMzeroService;
        MzeroPay?: IMzeroService;
        WebCamera?: IMzeroService;
    };
}

interface IMzero {
    create: ICreate;
}

interface ICreate {
    MAPEnv: {
        isInitialized: () => boolean;
        connected: () => boolean;
        initialize: (
            options: {
                Connection: {
                    Url: string;
                    Login: string;
                    Passcode: string;
                };
                AppData: {
                    // optional application data
                    MachineId1: string;
                    SoftwareId: string;
                };
            },
            eventHandlers: IMzeroEventHandlers<{ MachineId1: string; }>
        ) => void;
        PlatformData: {
            MachineId: string;
        };
    };
    scanner: {
        scanLicense: () => MZRequest<ILicenseScanResult>;
        scanSymbol: () => MZRequest<IQRCodeScanResult>;
    };
    locker: IMzeroLocker;
    system?: {
        exitToDesktop: (reason?: string) => MZRequest<any>;
        report: () => MZRequest<IMzeroServiceMapping>;
    };
}

declare const mzero: IMzero;

enum Types {
    Initialized = 'INITIALIZED',
    Loaded = 'LOADED',
    Configured = 'CONFIGURED'
}

type MZeroPayload = {
    [Types.Initialized]: never;
    [Types.Loaded]: never;
    [Types.Configured]: never;
};

type MZeroActions = ActionMap<MZeroPayload>[keyof ActionMap<MZeroPayload>];

const internalStateReducer = (state: InternalStateType, action: MZeroActions) => {
    switch (action.type) {
        case Types.Loaded:
            return {
                loaded: true,
                initialized: false,
                configured: false,
            } as typeof state;
        case Types.Initialized:
            return {
                ...state,
                initialized: true,
                configured: false,
            } as typeof state;
        case Types.Configured:
            return {
                ...state,
                configured: true,
            } as typeof state;
        default:
            return state;
    }
};

export function MZeroProvider({ children }: MZeroProviderProps) {
    const [state, setState] = React.useState<InitialStateType>(initialState);
    const [internalState, internalReducer] = React.useReducer(internalStateReducer, {
        initialized: false,
        loaded: isDevelopment(),
        configured: false,
    });
    if (isElectron() && !internalState.loaded) {
        loadMZeroScripts()
            .then(() => internalReducer({ type: Types.Loaded }));
    }
    React.useEffect(() => {
        if (isElectron()) {
            if (internalState.loaded && !internalState.initialized) {
                if (isDevelopment()) {
                    internalReducer({ type: Types.Initialized });
                }
                else if (typeof mzero !== 'undefined' && !mzero.create.MAPEnv.isInitialized()) {
                    const appData = {
                        // optional application data
                        MachineId1: '',
                        SoftwareId: '',
                    };
                    const url = process.env.REACT_APP_MZERO_WS || 'ws://127.0.0.1:61623';
                    const options = {
                        Connection: {
                            Url: url,
                            Login: 'mapkiosk',
                            Passcode: 'oY8tKFDPCQG4KG',
                        },
                        AppData: appData,
                    };
                    const eventHandlers = {
                        Done: (err: string, data: { MachineId1: string; }) => {
                            console.log('mzero|initialized');
                            err && console.warn(err);
                            internalReducer({ type: Types.Initialized });
                            setState({
                                mzero: mzero,
                                error: err,
                            });
                        },
                    };

                    try {
                        console.log('mzero|Attempting MZero connection');
                        mzero.create.MAPEnv.initialize(options, eventHandlers);
                    }
                    catch (err) {
                        console.error(`Tried to initialize mzero environment, but that failed due to error ${JSON.stringify(err)}`);
                    }
                }
            }
        }
        else {
            throw new Error('MZeroProvider must be used within an Electron instance');
        }
    }, [internalState.loaded, internalState.initialized]);

    const configure = (mzero?: IMzero) =>
        new Promise<Pick<InitialStateType, 'lockersEnabled' | 'licenseScanEnabled'>>((resolve, reject) => {
            if (isDevelopment()) {
                resolve({ lockersEnabled: true });
            }
            else if (mzero) {
                let responseReceived = false;
                let abandoned = false;

                console.log('mzero|initialized but not connected');
                const eventHandlers = {
                    Done: (err: string, data: IMzeroServiceMapping) => {
                        if (!abandoned) {
                            responseReceived = true;

                            console.log('mzero|connected: ', { err });
                            err && console.warn(err);
                            if (!err) {
                                console.log('mzero|connected without errors');
                                internalReducer({ type: Types.Configured });
                                const licenseScanEnabled = data.ServicesMapping.License?.State === 'Available';
                                const lockersEnabled = data.ServicesMapping.Locker?.State === 'Available';

                                resolve({
                                    licenseScanEnabled,
                                    lockersEnabled,
                                });
                            }
                            else {
                                reject(err);
                            }
                        }
                    },
                };
                console.log('mzero|state.create.MAPEnv.connected: ', mzero.create.MAPEnv.connected());
                if (mzero.create.MAPEnv.connected()) {
                    mzero.create.system?.report()
                        .start(eventHandlers);
                    /**
                     * The system report request sometimes doesn't make it to the map service
                     * and returns no indication for this. The call generally takes 1s to
                     * complete, but we'll wait for 30 and if no response was received,
                     * consider it a failure.
                     */
                    setTimeout(() => {
                        if (!responseReceived) {
                            abandoned = true;
                            reject('system report timeout');
                        }
                    }, 30000);
                }
                else {
                    reject('disconnected');
                }
            }
        });

    const refreshHardwareStatus = useCallback(async (mzero?: IMzero) => {
        if (mzero) {
            let attempt: number = 1;

            while (attempt <= 3) {
                await new Promise((r) => setTimeout(r, 5000));
                const result = await configure(mzero);

                setState((state) => ({
                    ...state,
                    ...result,
                }));

                if (result.lockersEnabled && result.licenseScanEnabled) {
                    break;
                }

                attempt++;
            }
        }
    }, []);

    React.useEffect(() => {
        if (!internalState.configured && internalState.initialized) {
            (async () => {
                try {
                    const result = await configure(state.mzero);
                    internalReducer({ type: Types.Configured });
                    setState((state) => ({
                        ...state,
                        connected: true,
                        ...result,
                    }));

                    /*
                     * Sometimes there is a delay between system startup and the hardware being
                     * available. Run the system report a few more times to ensure the
                     * correct status is obtained
                     */
                    if (!result.lockersEnabled || !result.licenseScanEnabled) {
                        refreshHardwareStatus(state.mzero);
                    }
                }
                catch (e) {
                    console.log(`mzero|configuration failed|${e}`);
                    console.log('refreshing...');
                    window.location.reload();
                }
            })();
        }
    }, [internalState.configured, internalState.initialized, state, refreshHardwareStatus]);

    return <MZeroStateContext.Provider value={state}>{children}</MZeroStateContext.Provider>;
}

/** Retrieves the Kiosk from the context */
export function useMZeroState() {
    return React.useContext(MZeroStateContext);
}

export function useMZeroMachineId() {
    const { connected, mzero } = useMZeroState();

    return {
        loading: isElectron() && !connected,
        machineId: mzero?.create.MAPEnv.PlatformData.MachineId,
    };
}

// #region Scanner

export enum ScanErrorCode {
    unknown = 0,
    mzeroError,
    timeout,
    cancelled,
    inUse,
    activation,
    badData,
    scannerNotFound
}

export interface IScanError {
    code: ScanErrorCode;
    message?: string;
}

const useScan = <TResult extends any>(props: { start?: () => MZRequest<TResult>; }) => {
    const { mzero, error: mzError } = useMZeroState();

    const [error, setError] = React.useState<IScanError>();
    const [inProgress, setInProgress] = React.useState(false);
    const [result, setResult] = React.useState<TResult>();
    const [mzRequest, setMZRequest] = React.useState<MZRequest<TResult>>();

    const start = () => {
        if (props.start && !mzError && !error && mzero && !inProgress) {
            try {
                setInProgress(true);
                setMZRequest(props.start());
            }
            catch (err) {
                setError({ code: ScanErrorCode.unknown });
                console.log(err);
            }
        }
    };

    React.useEffect(() => {
        if (mzRequest) {
            const eventHandlers = {
                Done: function (err: string, data: TResult) {
                    if (err) {
                        if (err.toLowerCase()
                            .indexOf('timeout') > -1) {
                            setError({
                                code: ScanErrorCode.timeout,
                                message: err,
                            });
                            console.log('scan timeout');
                        }
                        else if (err.toLowerCase()
                            .indexOf('cancelled') > -1) {
                            setError({
                                code: ScanErrorCode.cancelled,
                                message: err,
                            });
                            // cancelled scan function.
                            console.log('cancelled scanner');
                        }
                        else if (err.toLowerCase()
                            .indexOf('serviceinuse') > -1) {
                            setError({
                                code: ScanErrorCode.inUse,
                                message: err,
                            });
                            console.error('error', 'Error activating scanner ' + err);
                        }
                        else {
                            setError({
                                code: ScanErrorCode.activation,
                                message: err,
                            });
                            console.error('error', 'Error activating scanner ' + err);
                        }
                    }
                    else if (!data) {
                        setError({ code: ScanErrorCode.badData });
                        console.log('error', 'Data is null.');
                    }
                    else {
                        mzRequest?.end();
                        setResult(data);
                    }
                    setInProgress(false);
                },
            };
            mzRequest.start(eventHandlers);
        }

        return mzRequest?.end;
    }, [mzRequest]);

    return {
        /** The error if one was encountered */
        error: mzError ? {
            code: ScanErrorCode.mzeroError,
            message: mzError,
        } : error,
        setError,
        /** The scanned result object */
        result,
        /** Initiates the scanner */
        start,
        /** Terminates the scanner if running */
        stop: () => mzRequest?.end(),
        /** True if the scanner is in progress */
        inProgress,
        reset: () => {
            mzRequest?.end();
            setError(undefined);
            setResult(undefined);
            setInProgress(false);
        },
        simulateError:
            (isDevelopment() &&
                ((errorCode: ScanErrorCode) => {
                    mzRequest?.end();
                    setError({ code: errorCode });
                    setResult(undefined);
                    setInProgress(false);
                })) ||
            undefined,
        simulateResult:
            (isDevelopment() &&
                ((result: TResult) => {
                    mzRequest?.end();
                    setError(undefined);
                    setResult(result);
                    setInProgress(false);
                })) ||
            undefined,
    };
};

// #region License Scan
export interface ILicenseScanResult {
    FirstName: string;
    LastName: string;
    StreetAddress: string;
    City: string;
    StateName: string;
    PostalCode: string;
}
export const useMZeroScanLicense = () => {
    const { mzero, licenseScanEnabled = false } = useMZeroState();
    const scanner = useScan({ start: mzero?.create.scanner.scanLicense });

    const simulateResult = () => {
        const names = [
            'KRIS',
            'THOMAS',
            'RICHARD',
            'STEVENS',
            'BROWN',
            'JOHN',
            'ROBERT',
            'SHAFT',
            'LIZARD',
            'BOB',
            'ALIENS',
            'WATTENBURGER',
            'ELIZABETH',
            'VON HOMENHEIM',
            'SHEPARD',
            'NORMAN',
        ];
        const result: ILicenseScanResult = {
            FirstName: names[Math.floor(Math.random() * names.length)],
            LastName: names[Math.floor(Math.random() * names.length)],
            StreetAddress: '123 Street Road',
            City: 'Place',
            StateName: 'WY',
            PostalCode: '12345-5678',
        };
        scanner.simulateResult?.(result);
    };

    return {
        ...scanner,
        rawResult: undefined,
        licenseScanEnabled,
        simulateResult,
    };
};

// #region QR Scanner
export interface IQRCodeScanResult {
    QRCode: string;
}

export type QRCodeData = {
    key?: string;
    dealerKey?: string;
    dmsDealerKey?: string;
    dmsStoreId?: string;
    dmsAreaId?: string;
    apptNo?: number;
    currentUrl?: string;
    mobileCheckInComplete?: boolean;
    maskPhone?: boolean;
    maskEmail?: boolean;
    maskVin?: boolean;
    phoneType?: number;
    /**
     * Locker the advisor selected in dashboard Key Management. Present on advisor
     * "Add Key" QR codes for 365/express kiosks. Used to request that exact slot
     * when storing the key instead of letting the server auto-assign.
     */
    lockerNumber?: number;
} & ({ key: string; apptNo?: never; } | { key?: never; apptNo: number; });
export const useMZeroQRScanner = (saveResultAsCheckIn: boolean = true) => {
    const { mzero } = useMZeroState();
    const { setError, ...scanner } = useScan({ start: mzero?.create.scanner.scanSymbol });

    const [scanResult, setScanResult] = React.useState('');
    const [result, setResult] = React.useState<QRCodeData>();

    React.useEffect(() => {
        if (scanner.result?.QRCode) {
            setScanResult(scanner.result.QRCode.trim());
        }
    }, [scanner.result]);

    React.useEffect(() => {
        if (saveResultAsCheckIn && scanResult) {
            if (scanResult.startsWith('{')) {
                setResult(JSON.parse(scanResult));
            }
            else if (scanResult.includes('|')) {
                const parsed = scanResult.match(/[^\s|]+/g) || [];
                const apptNo = parseInt(parsed[parsed.length - 1], 10);
                if (parsed.length <= 2) {
                    // GoMotoCore QR code for DMS appt. w/ or w/o dealer key
                    const dealerKey = parsed[parsed.length - 2];
                    setResult({
                        apptNo,
                        ...(dealerKey && { dealerKey }),
                    });
                }
                else {
                    // CR+ QR code
                    const [dmsDealerKey, dmsStoreId, dmsAreaId] = parsed;
                    setResult({
                        apptNo,
                        dmsDealerKey,
                        dmsStoreId,
                        dmsAreaId,
                    });
                }
            }
            else {
                setError({ code: ScanErrorCode.badData });
            }
        }
    }, [saveResultAsCheckIn, scanResult, setError]);

    return {
        ...scanner,
        reset: () => {
            setResult(undefined);
            setScanResult('');
            scanner.reset();
        },
        /** The scanned result value */
        result,
        /** The scanned result as raw data */
        rawResult: scanResult,
        /** Clears out the result value */
        clearResult: () => {
            setResult(undefined);
            setScanResult('');
        },
        /** In a DEV only environment, forces a specific result */
        setResult: process.env.NODE_ENV !== 'production' ? (result: string) => setScanResult(result) : undefined,
    };
};
/*
 * #endregion
 *  #endregion
 */

// #region Scripts
enum MZeroScript {
    stomp,
    create,
    report,
    presence,
    log4javascript,
    'plugins.logging.log4javascript',
    locker,
    payment,
    printer,
    scanner,
    system,
    'components/mailer',
    'components/screentracker'
}
const reversed = reverseEnum(MZeroScript);

const loadMZeroScript = (script: MZeroScript) => loadScript(`/mzapi/${reversed[script]}.js`, reversed[script]);

const loadMZeroScripts = () =>
    loadMZeroScript(MZeroScript.stomp)
        .then(() => loadMZeroScript(MZeroScript.create))
        .then(() =>
            Promise.all([
                loadMZeroScript(MZeroScript.report),
                loadMZeroScript(MZeroScript.presence),
                loadMZeroScript(MZeroScript.locker),
                loadMZeroScript(MZeroScript.payment),
                loadMZeroScript(MZeroScript.printer),
                loadMZeroScript(MZeroScript.scanner),
                loadMZeroScript(MZeroScript.system),
                loadMZeroScript(MZeroScript['components/mailer']),
                loadMZeroScript(MZeroScript['components/screentracker']),
            ]),
        );
// #endregion

// #region Locker

type MzeroLockerStoreHandlers<T = ILockerData> = IMzeroEventHandlers<T> & { Storing?: (err: string, data: T) => void; };
type MzeroLockerRetrieveHandlers<T = ILockerData> = IMzeroEventHandlers<T> & {
    Retrieving?: (err: string, data: T) => void;
    SlowRetrieval?: (err: string, data: T) => void;
};

type MzeroLockerRequest<TEventHandlers, TParams = ILockerParams> = (params: TParams) => {
    start: (eventHandler: TEventHandlers) => void;
} & IEndableRequest;

interface ILockerData {
    Lockers: ILockerFixture[];
}

interface IAdminDoorFixture {
    Door: number;
}

interface IMzeroLocker {
    checkLockers: () => {
        start: (eventHandler: MzeroLockerStoreHandlers<ILockerData>) => void;
    } & IEndableRequest;
    store: MzeroLockerRequest<MzeroLockerStoreHandlers<ILockerFixture>>;
    retrieve: MzeroLockerRequest<MzeroLockerRetrieveHandlers<Pick<ILockerFixture, 'Id' | 'Status'>>>;
    openDoor: MzeroLockerRequest<IMzeroEventHandlers<IAdminDoorFixture>, IAdminDoorFixture>;
}

export const useMZeroLocker = (onLockerOpen?: GoMotoFunction<[void], void>) => {
    const { lockers, error, loading } = useLockerContext();
    const { mzero, connected: mzeroConnected, lockersEnabled } = useMZeroState();
    const { keyStorageType } = useKioskState();
    const setLockerStatus = useSetLockerStatus();
    const storeRequest = useRef<
        | ({
            start: (eventHandler: MzeroLockerStoreHandlers<ILockerFixture>) => void;
        } & IEndableRequest)
        | undefined
    >();

    /** Retrieves the next available locker. */
    const storeInLocker = (id: number, appointmentKey?: string) =>
        new Promise<void>((resolve, reject) => {
            if (isDevelopment()) {
                if (lockers?.find((locker) => locker.Id === id)?.Status === 'Taken') {
                    reject(false);

                    return;
                }
                else {
                    const locker = lockers?.find((locker) => locker.Id === id);
                    if (locker?.Status === 'Available') {
                        setLockerStatus(locker, 'Taken');
                        resolve();
                    }
                    else {
                        reject(false);
                    }
                }
            }
            else {
                if (!mzeroConnected || !lockersEnabled || lockers?.find((locker) => locker.Id === id)?.Status === 'Taken') {
                    reject(false);

                    return;
                }
                else {
                    const params = {
                        Locker: id,
                        DoorClosureTimeout: 1800000,
                        APIReport: {
                            CustomRef1: 'appointmentKey',
                            CustomId1: appointmentKey,
                        },
                    };
                    storeRequest.current = mzero?.create.locker.store(params);
                    storeRequest.current?.start({
                        Done: (err) => {
                            if (err) {
                                reject(err);
                            }
                            else {
                                const locker = lockers?.find((locker) => locker.Id === id);
                                if (locker?.Status === 'Available') {
                                    setLockerStatus(locker, 'Taken');
                                    resolve();
                                }
                                else {
                                    reject(false);
                                }
                            }
                        },
                    });
                }
            }
        });

    const retrieveFromLocker = (id: ILockerFixture['Id'], appointmentKey?: IAppointment['key']) =>
        new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker = lockers?.find((locker) => locker.Id === id);
            if (isDevelopment()) {
                if (locker?.Status === 'Available') {
                    resolve(false);

                    return;
                }
                else {
                    if (locker?.Status === 'Taken') {
                        setLockerStatus(locker, 'Available');
                        resolve(locker);
                    }
                    else {
                        resolve(false);
                    }
                }
            }
            else {
                if (!mzeroConnected || !lockersEnabled || locker?.Status === 'Available') {
                    resolve(false);

                    return;
                }
                else {
                    const params = {
                        Locker: id,
                        DoorClosureTimeout: 0,
                        APIReport: {
                            CustomRef1: 'appointmentKey',
                            CustomId1: appointmentKey,
                        },
                    };
                    const request = mzero?.create.locker.retrieve(params);
                    request?.start({
                        Done: (err) => {
                            if (err) {
                                reject(err);
                            }
                            else {
                                if (locker?.Status === 'Taken') {
                                    setLockerStatus(locker, 'Available');
                                    resolve(locker);
                                }
                                else {
                                    resolve(false);
                                }
                            }
                        },
                    });
                }
            }
        });

    const openLocker = (id: ILockerFixture['Id']) =>
        new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker: ILockerFixture | undefined = lockers.find((locker: ILockerFixture) => locker.Id === id);
            if (isDevelopment()) {
                if (locker !== undefined) {
                    onLockerOpen?.();

                    resolve(locker);
                }
                else {
                    resolve(false);
                }
            }
            else if (locker && mzero && lockersEnabled && mzeroConnected) {
                mzero.create.locker.openDoor({ Door: id })
                    .start({
                        Done: (error: string) => {
                            if (error) {
                                reject(error);
                            }
                            else {
                                onLockerOpen?.();

                                resolve(locker);
                            }
                        },
                    });
            }
            else {
                resolve(false);
            }
        });

    const exchangeKeyAtLocker = (id: ILockerFixture['Id'], appointmentKey?: IAppointment['key']) =>
        new Promise<ILockerFixture | false>((resolve, reject) => {
            const locker: ILockerFixture | undefined = lockers.find((locker: ILockerFixture) => locker.Id === id);
            if (isDevelopment()) {
                if (locker?.Status === 'Available') {
                    resolve(false);

                    return;
                }
                else {
                    if (locker?.Status === 'Taken') {
                        setLockerStatus(locker, 'Available');
                        resolve(locker);
                    }
                    else {
                        resolve(false);
                    }
                }
            }
            else {
                if (locker && mzero && lockersEnabled && mzeroConnected) {
                    mzero.create.locker.openDoor({ Door: id })
                        .start({
                            Done: (error: string) => {
                                console.log(error);
                                if (error) {
                                    reject(error);
                                }
                                else {
                                    resolve(locker);
                                }
                            },
                        });
                }
                else {
                    resolve(false);
                }
            }
        });

    useEffect(
        () => () => {
            if (storeRequest.current) {
                storeRequest.current.end();
            }
        },
        [],
    );

    return {
        loading: loading || !mzeroConnected,
        /** Stores a key in the provided locker. */
        storeInLocker,
        /** Opens a locker for a key to be retrieved */
        retrieveFromLocker,
        /**
         * Opens and closes the locker door as an admin operation
         */
        openLocker,
        exchangeKeyAtLocker,
        /** The last error encountered */
        error,
        lockersEnabled: !!lockersEnabled && !!keyStorageType,
    };
};
// #endregion

// #region Card Reader
interface ICreate {
    payment: IPayment;
}

interface IPayment {
    pay: (payment: { OrderNumber: number; Currency: 'USD'; Amount: number; }) => IPaymentRequest;
}

interface IPaymentRequest {
    commit: (amount: number) => void;
    abort: () => void;
    start: (eventHandlers: IPaymentEventHandlers) => void;
}

interface IPaymentTransaction {
    StatusMessage: 'CONFIRM' | 'APPROVED' | 'PARTIAL APPROVAL' | 'VOICE REFERRAL' | 'DECLINED' | 'AP' | 'APPROVAL';
    SignatureVerificationRequired: boolean;
    SignatureCaptured: boolean;
    /**
     * Authorization Reference to uniquely identify the record
     */
    AuthReference: string;
    CardEaseReference: string;
    CardHash: string;
    CardReference: string;
    TransactionTime: Date;
    TotalAmount: string;
    PanMasked: string;
    ExpiryDate: string;
    MerchantName: string;
    CardScheme: string;
    TransactionSource: string;
    TransactionType: string;
    CardholderVerification: string;
    ApprovalCode: string;
    MerchantIdMasked: string;
    TerminalIdMasked: string;
    TransactionId: string;
    PaymentProvider: 'Moneris_CA' | 'Moneris_US' | 'CreditCall';
}

interface IPaymentEventHandlers<T = { PaymentTransaction?: IPaymentTransaction; }> extends IMzeroEventHandlers<T> {
    Confirm: (error: any, data: T) => void;
}

export interface ICardTransactionResult extends IPaymentTransaction {
    receiptNo: number;
}

export enum PaymentError {
    none = 0,
    unknown,
    declined,
    timeout,
    confirmationTimeout,
    partialApproval,
    voiceReferral,
    invalidValue,
    valueTooLarge,
    valueTooSmall,
    serviceNotAvailable,
    cancelled,
    serviceInUse,
    aborted
}

type InitialPaymentState = {
    mzero?: IMzero;
    status?: 'started' | 'confirmed' | 'complete' | 'committing';
    request?: IPaymentRequest;
    amount?: number;
    error?: PaymentError;
    results: ICardTransactionResult[];
};

enum PaymentTypes {
    Start = 'START',
    Confirm = 'CONFIRM',
    Commit = 'COMMIT',
    Complete = 'COMPLETE',
    Abort = 'ABORT',
    AddTransaction = 'ADD_TRANSACTION',
    Error = 'ERROR',
    Reset = 'RESET'
}

type PickRequired<T, K extends keyof T> = Required<Pick<T, K>>[K];

type PaymentPayload = {
    [PaymentTypes.Start]: { amount: PickRequired<InitialPaymentState, 'amount'>; eventHandlers: IPaymentEventHandlers; };
    [PaymentTypes.Confirm]: never;
    [PaymentTypes.Commit]: never;
    [PaymentTypes.AddTransaction]: PickRequired<InitialPaymentState, 'results'>[0];
    [PaymentTypes.Error]: PickRequired<InitialPaymentState, 'error'>;
    [PaymentTypes.Complete]: never;
    [PaymentTypes.Abort]: never;
    [PaymentTypes.Reset]: never;
};

type PaymentActions = ActionMap<PaymentPayload>[keyof ActionMap<PaymentPayload>];

const paymentStateReducer = (state: InitialPaymentState, action: PaymentActions): typeof state => {
    switch (action.type) {
        case PaymentTypes.Start:
            const request = state.mzero?.create.payment.pay({
                Amount: action.payload.amount,
                Currency: 'USD',
                OrderNumber: Date.now(),
            });
            request?.start(action.payload.eventHandlers);

            return {
                ...state,
                amount: action.payload.amount,
                request,
                status: 'started',
            };
        case PaymentTypes.Confirm:
            return {
                ...state,
                status: 'confirmed',
            };
        case PaymentTypes.Commit:
            state.amount && state.request && state.request.commit(state.amount);

            return {
                ...state,
                status: 'committing',
            };
        case PaymentTypes.Abort:
            state.request?.abort();

            return {
                mzero: state.mzero,
                status: 'complete',
                results: [],
            };
        case PaymentTypes.AddTransaction:
            return {
                ...state,
                results: [...state.results, action.payload],
            };
        case PaymentTypes.Error:
            return {
                ...state,
                error: action.payload,
                status: 'complete',
            };
        case PaymentTypes.Complete:
            return {
                ...state,
                status: 'complete',
            };
        case PaymentTypes.Reset:
            return {
                mzero: state.mzero,
                results: [],
            };
        default:
            return state;
    }
};

const useCheckCardReaderEnabled = () => {
    const { mzero } = useMZeroState();
    const [enabled, setEnabled] = useState<boolean | undefined>();

    return new Promise<boolean>((resolve) => {
        if (enabled) {
            resolve(enabled);
        }
        else if (isDevelopment()) {
            resolve(true);
        }

        const eventHandlers = {
            Done: (err: string, data: IMzeroServiceMapping) => {
                err && console.warn(err);
                if (!err) {
                    const cardReaderEnabled = data.ServicesMapping.MzeroPay?.State === 'Available';
                    setEnabled(cardReaderEnabled);
                    resolve(cardReaderEnabled);
                }
            },
        };

        if (mzero?.create.MAPEnv.connected()) {
            mzero.create.system?.report()
                .start(eventHandlers);
        }
        else {
            resolve(false);
        }
    });
};

export const useMZeroCardReader = () => {
    const { mzero } = useMZeroState();
    const cardReaderEnabled = useCheckCardReaderEnabled();

    const [state, dispatch] = React.useReducer(paymentStateReducer, {
        mzero,
        results: [],
    });

    const eventHandlers: IPaymentEventHandlers = {
        Confirm: function (err, { PaymentTransaction }) {
            if (PaymentTransaction?.StatusMessage === 'APPROVED') {
                dispatch({ type: PaymentTypes.Commit });
            }
            else {
                dispatch({ type: PaymentTypes.Abort });
            }
        },
        Done: function (err, { PaymentTransaction }) {
            if (!err && PaymentTransaction) {
                if (PaymentTransaction.CardScheme !== '') {
                    dispatch({
                        type: PaymentTypes.AddTransaction,
                        payload: {
                            ...PaymentTransaction,
                            receiptNo: Date.now(),
                        },
                    });
                }

                if (PaymentTransaction.StatusMessage.toLowerCase() === 'confirm') {
                    dispatch({ type: PaymentTypes.Complete });
                }
            }
            else if (err) {
                const errorMessage = err.toLowerCase();
                const statusMessage = PaymentTransaction?.StatusMessage.toLowerCase();
                let error = PaymentError.none;
                if (statusMessage === 'declined') {
                    error = PaymentError.declined;
                }
                else if (statusMessage === 'partial approval') {
                    error = PaymentError.partialApproval;
                }
                else if (statusMessage === 'voice referral') {
                    error = PaymentError.partialApproval;
                }
                else if (errorMessage === 'invalidvalue') {
                    error = PaymentError.invalidValue;
                }
                else if (errorMessage === 'ordernumbertoolarge') {
                    error = PaymentError.valueTooLarge;
                }
                else if (errorMessage === 'timeout') {
                    error = PaymentError.timeout;
                }
                else if (errorMessage === 'servicenotavailable') {
                    error = PaymentError.serviceNotAvailable;
                }
                else if (errorMessage === 'confirmationtimeout') {
                    error = PaymentError.confirmationTimeout;
                }
                else if (errorMessage === 'cancelled') {
                    error = PaymentError.cancelled;
                }
                else if (errorMessage === 'serviceinuse') {
                    error = PaymentError.serviceInUse;
                }
                else if (errorMessage === 'aborted') {
                    error = PaymentError.aborted;
                }
                else {
                    error = PaymentError.unknown;
                }
                dispatch({
                    type: PaymentTypes.Error,
                    payload: error,
                });
            }
        },
    };

    const pay = (amount: number) => {
        if (!state.error && (!state.status || state.status === 'complete')) {
            if (!amount || amount < 1) {
                dispatch({
                    type: PaymentTypes.Error,
                    payload: PaymentError.valueTooSmall,
                });
            }
            else {
                if (isDevelopment()) {
                    const transaction: IPaymentTransaction = {
                        StatusMessage: 'CONFIRM',
                        SignatureVerificationRequired: false,
                        SignatureCaptured: false,
                        AuthReference: '',
                        CardEaseReference: '',
                        CardHash: '',
                        CardReference: '',
                        TransactionTime: new Date(),
                        TotalAmount: '',
                        PanMasked: '************1234',
                        ExpiryDate: '',
                        MerchantName: 'MZERO Test Merchant',
                        CardScheme: 'DINERS',
                        TransactionSource: '',
                        TransactionType: '',
                        CardholderVerification: '',
                        ApprovalCode: '98E7D2',
                        MerchantIdMasked: '****56789',
                        TerminalIdMasked: '****1220',
                        TransactionId: '',
                        PaymentProvider: 'Moneris_US',
                    };
                    dispatch({
                        type: PaymentTypes.AddTransaction,
                        payload: {
                            ...transaction,
                            receiptNo: Date.now(),
                        },
                    });
                    dispatch({ type: PaymentTypes.Complete });
                }
                else {
                    dispatch({
                        type: PaymentTypes.Start,
                        payload: {
                            amount,
                            eventHandlers,
                        },
                    });
                }
            }
        }
    };

    return {
        cardReaderEnabled,
        pay,
        loading: state.status && state.status !== 'complete',
        abort: () => dispatch({ type: PaymentTypes.Abort }),
        reset: () => dispatch({ type: PaymentTypes.Reset }),
        error: state.error,
        results: state.results,
    };
};
/*
 * #endregion
 * #region Exit to desktop
 */
export const useMZeroExitToDesktop = () => {
    const { mzero } = useMZeroState();
    const exit = (reason?: string) => {
        console.log('We are in useExitToDesktop. Just about to call mzero.');
        mzero?.create.system?.exitToDesktop(reason)
            .start({
                Done: (err, data) => {
                    console.warn('Exit to desktop Done:');
                    console.warn(err);
                    console.warn(data);
                },
            });
    };

    return { exit };
};
// #endregion
